import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BreakdownBarList } from './BreakdownBarList';
import type { DomainSkillGroup } from '../../types/activity-overview';

interface SkillBreakdownProps {
  groups: DomainSkillGroup[];
}

/**
 * By Domain Skill — skills grouped under their owning domain. Excluded skills
 * carry a badge; cross-domain usage is counted under the owning domain.
 */
export function SkillBreakdown({ groups }: SkillBreakdownProps) {
  return (
    <Card className="bg-pine-card border-pine-line shadow-pine-card rounded-pine-card">
      <CardHeader>
        <CardTitle className="text-heading-4 text-pine-ink">By Domain Skill</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {groups.map((group) => (
          <div key={group.id} className="space-y-2">
            <h3 className="text-sm font-semibold text-pine-ink flex items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-pine-pine" />
              {group.title}
            </h3>
            <BreakdownBarList
              rows={group.skills.map((skill) => ({
                key: String(skill.id),
                label: (
                  <span className="flex items-center gap-2">
                    <span className="truncate">{skill.title}</span>
                    {skill.isExcluded && <Badge variant="secondary">Excluded</Badge>}
                  </span>
                ),
                primary: skill.activityCount,
                secondary: skill.stageAssignmentCount,
                secondaryLabel: 'active stages',
              }))}
            />
          </div>
        ))}
        <p className="text-caption text-pine-ink-soft">
          Some domain skills are excluded and are not used by the recommendation engine. Skills are
          assigned per stage and may be used cross-domain; usage is counted under the skill&apos;s
          owning domain.
        </p>
      </CardContent>
    </Card>
  );
}
